import { useEffect, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, Headphones, Loader2, Mic, Volume2 } from "lucide-react";
import {
  useMicrophonePermission,
  type MicPermissionState,
} from "@/features/interview/hooks/useMicrophonePermission";
import { isIOSDevice } from "@/shared/lib/fullscreen";
import { AudioLevelMeter } from "./AudioLevelMeter";
import { PermissionStatus, type ChecklistState } from "./PermissionStatus";

interface PreInterviewCheckProps {
  busy: boolean;
  /** Called only after every check passes and the candidate clicks Start. */
  onProceed?: () => void;
  /**
   * When false, the panel omits its own "Start interview" button and instead
   * reports readiness via `onReadyChange` so a parent (e.g. the Intro screen)
   * can gate its own Start button. Defaults to true.
   */
  showStartButton?: boolean;
  /** Notifies the parent whenever all checks pass / stop passing. */
  onReadyChange?: (ready: boolean) => void;
  /** Exposes lobby mic cleanup so the parent Start button can release getUserMedia. */
  onStopMonitoringReady?: (stop: () => void) => void;
}

const BLOCKED_STATES: MicPermissionState[] = [
  "denied",
  "nodevice",
  "insecure",
  "unsupported",
  "error",
];

function blockedMessage(state: MicPermissionState): string {
  const ios = isIOSDevice();
  switch (state) {
    case "denied":
      return ios
        ? "Microphone access is blocked. Open Settings → Safari → Microphone, set this site to Allow, then return here and press Retry."
        : "Microphone access is blocked. Tap the lock or tune icon in the address bar, set Microphone to Allow, then press Retry.";
    case "nodevice":
      return "No microphone was found. Please connect a microphone (or check your device settings), then press Retry.";
    case "insecure":
      return "The microphone can only be used over a secure (https) connection. Please open this page via https and retry.";
    case "unsupported":
      return "This browser does not support microphone access. Please use a recent version of Chrome, Edge, or Safari.";
    default:
      return "We couldn't access the microphone. Please close other apps that may be using it, then press Retry.";
  }
}

/** Short test tone so candidates verify speaker/headphone output before the interview. */
function playSpeakerTestTone() {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    void ctx.resume?.();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 440;
    gain.gain.value = 0.15;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.35);
    osc.onended = () => {
      void ctx.close?.();
    };
  } catch {
    /* best-effort — checkbox fallback remains */
  }
}

/**
 * Google-Meet-style pre-interview lobby. Permission is requested on an explicit
 * "Enable microphone" click; the interview only starts once permission is
 * granted, audio input is detected, and the candidate confirms speaker output.
 */
export function PreInterviewCheck({
  busy,
  onProceed,
  showStartButton = true,
  onReadyChange,
  onStopMonitoringReady,
}: PreInterviewCheckProps) {
  const {
    permissionState,
    audioInputDetected,
    audioLevel,
    deviceLabel,
    requestPermission,
    retry,
    stopMonitoring,
  } = useMicrophonePermission();

  const [requesting, setRequesting] = useState(false);
  const [speakerConfirmed, setSpeakerConfirmed] = useState(false);
  const [speakerTested, setSpeakerTested] = useState(false);

  const isGranted = permissionState === "granted";
  const isBlocked = BLOCKED_STATES.includes(permissionState);

  const micState: ChecklistState = requesting
    ? "loading"
    : isGranted
      ? "success"
      : isBlocked
        ? "failed"
        : "pending";

  const audioState: ChecklistState = audioInputDetected
    ? "success"
    : isGranted
      ? "loading"
      : "pending";

  const speakerState: ChecklistState = speakerConfirmed ? "success" : "pending";

  const micHint = useMemo(() => {
    if (isGranted) return deviceLabel || undefined;
    if (permissionState === "prompt") return "Click “Enable microphone” to allow access.";
    if (isBlocked) return "Access is blocked — allow it, then retry.";
    return undefined;
  }, [isGranted, isBlocked, permissionState, deviceLabel]);

  const ready = isGranted && audioInputDetected && speakerConfirmed;
  const canStart = ready && !busy;

  useEffect(() => {
    onReadyChange?.(ready);
  }, [ready, onReadyChange]);

  useEffect(() => {
    onStopMonitoringReady?.(stopMonitoring);
  }, [stopMonitoring, onStopMonitoringReady]);

  const handleEnable = async () => {
    setRequesting(true);
    try {
      await requestPermission();
    } finally {
      setRequesting(false);
    }
  };

  const handleRetry = async () => {
    setRequesting(true);
    try {
      await retry();
    } finally {
      setRequesting(false);
    }
  };

  const handleSpeakerTest = () => {
    playSpeakerTestTone();
    setSpeakerTested(true);
  };

  const handleProceed = () => {
    // Release the lobby's mic/AudioContext so the speech engine owns the device.
    stopMonitoring();
    onProceed?.();
  };

  return (
    <div className="flex flex-col items-center gap-5 my-4 w-full mx-auto max-w-md sm:max-w-none">
      <div className="w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-4 sm:p-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center shrink-0">
            <Mic className="w-5 h-5 text-brand-600" />
          </div>
          <div>
            <h2 className="text-lg font-black text-ink-900 leading-tight">
              Prepare for your interview
            </h2>
            <p className="text-xs text-ink-400">Before starting, complete the following checks.</p>
          </div>
        </div>

        <div className="mt-4 bg-brand-50 border border-brand-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <Headphones className="w-5 h-5 text-brand-600 shrink-0" />
          <p className="text-ink-600 text-xs">
            Use headphones for the best audio quality — it also prevents echo during your answers.
          </p>
        </div>

        <div className="mt-5 space-y-3">
          <PermissionStatus label="Microphone permission" state={micState} hint={micHint} />
          <PermissionStatus
            label="Audio input detected"
            state={audioState}
            hint={
              isGranted && !audioInputDetected ? "Speak to test your microphone." : undefined
            }
          />
          <PermissionStatus
            label="Speaker ready"
            state={speakerState}
            hint={!speakerConfirmed ? "Play the test tone, then confirm below." : undefined}
          />
        </div>

        {isGranted && (
          <div className="mt-4">
            <AudioLevelMeter level={audioLevel} active={audioInputDetected} />
            <p className="text-xs text-ink-400 mt-2">
              {audioInputDetected
                ? "Great — we can hear you."
                : "Say “hello, testing” — the bar should move."}
            </p>
          </div>
        )}

        {isGranted && (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <button
              type="button"
              onClick={handleSpeakerTest}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-ink-800 text-sm font-bold transition-colors"
            >
              <Volume2 className="w-4 h-4" aria-hidden="true" />
              Test speaker
            </button>
            {speakerTested ? (
              <p className="text-xs text-green-700 font-semibold">Test tone played — did you hear it?</p>
            ) : (
              <p className="text-xs text-ink-400">Tap to play a short tone through your speaker or headphones.</p>
            )}
          </div>
        )}

        {isGranted && (
          <label className="mt-4 flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={speakerConfirmed}
              onChange={(e) => setSpeakerConfirmed(e.target.checked)}
              className="w-4 h-4 mt-0.5 accent-brand-600 shrink-0"
            />
            <span className="text-sm text-ink-700">
              I can hear audio from my speaker / headphones
            </span>
          </label>
        )}

        {isBlocked && (
          <div className="mt-4 flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{blockedMessage(permissionState)}</p>
          </div>
        )}
      </div>

      {/* Actions */}
      {isGranted ? (
        <div className="w-full flex flex-col items-center gap-2">
          <div className="inline-flex items-center gap-1.5 text-green-600 text-sm font-bold">
            <CheckCircle2 className="w-4 h-4" /> Microphone ready
          </div>
          {showStartButton ? (
            <button
              type="button"
              onClick={handleProceed}
              disabled={!canStart}
              className="w-full px-10 py-4 rounded-2xl bg-brand-600 hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400 text-white font-black text-lg shadow-2xl"
            >
              Start interview
            </button>
          ) : null}
          {!ready ? (
            <p className="text-xs text-ink-400 text-center">
              {!audioInputDetected
                ? "Speak once so we can detect your microphone."
                : !speakerConfirmed
                  ? "Confirm you can hear audio to continue."
                  : ""}
            </p>
          ) : null}
        </div>
      ) : isBlocked ? (
        <button
          type="button"
          onClick={handleRetry}
          disabled={requesting || permissionState === "unsupported"}
          className="px-8 py-3 rounded-2xl bg-brand-600 hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400 text-white font-bold text-base shadow-lg inline-flex items-center gap-2"
        >
          {requesting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Retrying…
            </>
          ) : (
            "Retry"
          )}
        </button>
      ) : (
        <button
          type="button"
          onClick={handleEnable}
          disabled={requesting}
          className="px-10 py-4 rounded-2xl bg-brand-600 hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400 text-white font-black text-lg shadow-2xl inline-flex items-center gap-2"
        >
          {requesting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> Requesting…
            </>
          ) : (
            <>
              <Mic className="w-5 h-5" /> Enable microphone
            </>
          )}
        </button>
      )}
    </div>
  );
}
